import React, { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Settings as SettingsIcon,
  Build as ToolIcon,
  ExpandMore as ExpandMoreIcon,
  Speed as SpeedIcon,
  DataUsage as TokenIcon,
  IntegrationInstructions as IntegrationIcon,
  Lightbulb as TipIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import Markdown from 'react-markdown';
import { TechnicalOutput, ToolDefinition } from '../../../services/workflow';
import { exportTechnicalSpecToPdf } from '../../../utils/pdfExport';

interface TechnicalSpecEditorProps {
  technical: TechnicalOutput | null;
  qualityScore?: number;
  qualityScoreLabel?: string;
  isLoading?: boolean;
}

// Convert technical spec to markdown
const technicalToMarkdown = (tech: TechnicalOutput): string => {
  const tools = tech.tools || [];
  const toolList =
    tools.length > 0
      ? tools
          .map((t) => {
            const params =
              Array.isArray(t.suggested_params) && t.suggested_params.length > 0
                ? t.suggested_params.map((p) => `  - \`${p}\``).join('\n')
                : '  - None specified';
            return `### ${t.name}\n**Type:** ${t.type || 'function'}\n**Description:** ${t.description || 'No description'}\n**Async:** ${t.async ? 'Yes' : 'No'}\n\n**Parameters:**\n${params}`;
          })
          .join('\n\n---\n\n')
      : 'No tools defined yet.';

  const integrations = tech.integration_patterns || [];
  const integrationList =
    integrations.length > 0
      ? integrations.map((i) => `- ${i}`).join('\n')
      : '- Direct API calls with error handling';

  const sessionConfig = tech.session_config || {
    service: '',
    db_url: null,
    ttl_hours: 24,
  };
  const tokenEstimates = tech.token_estimates || {};
  const monthlyEstimates = tokenEstimates.monthly_estimates;
  const optimizationTips = tokenEstimates.optimization_tips || [];

  const formatNumber = (n: number | undefined) =>
    n !== undefined ? n.toLocaleString() : 'TBD';

  return `# Technical Architecture Specification

## Model Selection

### Recommended Model
**${tech.model || 'Not selected'}**

### Rationale
${tech.model_rationale || 'No rationale provided yet.'}

---

## Tools & Functions

${toolList}

---

## Memory & Session Strategy

### Strategy
**${tech.memory_strategy || 'Not specified'}**

### Configuration
\`\`\`
Service: ${sessionConfig.service || 'InMemorySessionService'}
TTL Hours: ${sessionConfig.ttl_hours || 24}
Persistence: ${sessionConfig.persistence_enabled ? 'Enabled' : 'Disabled'}
\`\`\`

---

## Token Usage Estimates

### Monthly Projections
- **Input Tokens**: ${formatNumber(monthlyEstimates?.input_tokens)}
- **Output Tokens**: ${formatNumber(monthlyEstimates?.output_tokens)}
- **Total**: ${formatNumber(monthlyEstimates?.total_tokens)}

### Optimization Tips
${optimizationTips.length > 0 ? optimizationTips.map((tip) => `- ${tip}`).join('\n') : '- No tips yet'}

---

## Integration Patterns

${integrationList}

---
*Generated by Technical Architect*
`;
};

// Tool item component with expand/collapse
const ToolItem: React.FC<{ tool: ToolDefinition }> = ({ tool }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToolIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">{tool.name}</Typography>
            <Chip
              label={tool.type || 'function'}
              size="small"
              variant="outlined"
            />
            {tool.async && (
              <Chip
                label="async"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: '0.2s',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {tool.description || 'No description'}
        </Typography>
        <Collapse in={expanded}>
          <Box sx={{ mt: 1, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight="bold">
              Parameters:
            </Typography>
            {tool.suggested_params && tool.suggested_params.length > 0 ? (
              <List dense disablePadding>
                {tool.suggested_params.map((param, idx) => (
                  <ListItem key={idx} sx={{ py: 0, pl: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {param}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', pl: 1 }}
              >
                No parameters specified
              </Typography>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const TechnicalSpecEditor: React.FC<TechnicalSpecEditorProps> = ({
  technical,
  qualityScore,
  qualityScoreLabel = 'Completeness',
  isLoading = false,
}) => {
  const [tab, setTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Empty state
  if (!technical) {
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <SettingsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          The Technical Architect will define model selection, tools, and memory
          strategy.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          The specification will appear here shortly.
        </Typography>
      </Paper>
    );
  }

  const markdown = technicalToMarkdown(technical);
  const tools = technical.tools || [];
  const sessionConfig = technical.session_config || {
    service: '',
    db_url: null,
    ttl_hours: 24,
  };
  const tokenEstimates = technical.token_estimates || {};
  const monthlyEstimates = tokenEstimates.monthly_estimates;
  const optimizationTips = tokenEstimates.optimization_tips || [];
  const integrations = technical.integration_patterns || [];

  const formatNumber = (n: number | undefined) =>
    n !== undefined ? n.toLocaleString() : 'TBD';

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with quality score */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Technical Specification
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={technical.model || 'No model'}
              size="small"
              color="primary"
              variant="outlined"
            />
            {qualityScore !== undefined && (
              <Chip
                label={`${qualityScoreLabel}: ${Math.round(qualityScore * 100)}%`}
                size="small"
                color={
                  qualityScore >= 0.8
                    ? 'success'
                    : qualityScore >= 0.5
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
              />
            )}
            <Tooltip title="Export as PDF">
              <IconButton
                size="small"
                onClick={() => exportTechnicalSpecToPdf(technical)}
              >
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {qualityScore !== undefined && (
          <LinearProgress
            variant="determinate"
            value={qualityScore * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  qualityScore >= 0.8
                    ? 'success.main'
                    : qualityScore >= 0.5
                      ? 'warning.main'
                      : 'grey.400',
              },
            }}
          />
        )}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="technical spec tabs"
        >
          <Tab label="Preview" />
          <Tab label="Details" />
        </Tabs>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {tab === 0 ? (
          // Preview tab - styled markdown
          <Box
            className="markdown-preview"
            sx={{
              '& h1': {
                fontSize: '1.5rem',
                mb: 2,
                color: 'primary.main',
                fontWeight: 600,
              },
              '& h2': {
                fontSize: '1.2rem',
                mt: 3,
                mb: 1,
                color: 'text.primary',
                borderBottom: 1,
                borderColor: 'divider',
                pb: 0.5,
                fontWeight: 600,
              },
              '& h3': { fontSize: '1rem', mt: 2, mb: 0.5, fontWeight: 600 },
              '& p': { mb: 1, lineHeight: 1.6 },
              '& ul': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
              '& hr': { my: 2, borderColor: 'divider' },
              '& strong': { fontWeight: 600 },
              '& code': {
                bgcolor: 'grey.100',
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.875em',
              },
              '& pre': {
                bgcolor: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
              },
            }}
          >
            <Markdown>{markdown}</Markdown>
          </Box>
        ) : (
          // Details tab - structured view
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Model Selection */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Model Selection
              </Typography>
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <SpeedIcon color="primary" />
                    <Typography variant="h6">
                      {technical.model || 'Not selected'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {technical.model_rationale || 'No rationale provided yet.'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Divider />

            {/* Tools */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Tools & Functions ({tools.length})
              </Typography>
              <Box sx={{ mt: 1 }}>
                {tools.length > 0 ? (
                  tools.map((tool, idx) => <ToolItem key={idx} tool={tool} />)
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No tools defined yet.
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Memory Strategy */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Memory & Session Strategy
              </Typography>
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <MemoryIcon color="action" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {technical.memory_strategy || 'Not specified'}
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText
                        primary="Service"
                        secondary={
                          sessionConfig.service || 'InMemorySessionService'
                        }
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                        }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText
                        primary="TTL"
                        secondary={`${sessionConfig.ttl_hours || 24} hours`}
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                        }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0 }}>
                      <ListItemText
                        primary="Persistence"
                        secondary={
                          sessionConfig.persistence_enabled
                            ? 'Enabled'
                            : 'Disabled'
                        }
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                        }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Box>

            <Divider />

            {/* Token Estimates */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Token Usage Estimates
              </Typography>
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <TokenIcon color="action" />
                    <Typography variant="subtitle2">
                      Monthly Projections
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}
                  >
                    <Chip
                      label={`Input: ${formatNumber(monthlyEstimates?.input_tokens)}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Output: ${formatNumber(monthlyEstimates?.output_tokens)}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Total: ${formatNumber(monthlyEstimates?.total_tokens)}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  {optimizationTips.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <TipIcon fontSize="inherit" /> Optimization Tips
                      </Typography>
                      <List dense disablePadding>
                        {optimizationTips.map((tip, idx) => (
                          <ListItem key={idx} sx={{ py: 0, pl: 1 }}>
                            <ListItemText
                              primary={tip}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Divider />

            {/* Integration Patterns */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Integration Patterns ({integrations.length})
              </Typography>
              <List dense>
                {integrations.length > 0 ? (
                  integrations.map((pattern, idx) => (
                    <ListItem key={idx} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <IntegrationIcon color="action" />
                      </ListItemIcon>
                      <ListItemText primary={pattern} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <IntegrationIcon color="action" />
                    </ListItemIcon>
                    <ListItemText primary="Direct API calls with error handling" />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TechnicalSpecEditor;
